import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Facebook } from "lucide-react";
import PageBanner from "../components/PageBanner";
import Seo from "../components/Seo";
import { Reveal, SectionLabel } from "../components/Motion";
import { useApp, api } from "../AppContext";
import { C, TRUST, UI, IMAGES } from "../content";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

export default function Contact() {
  const { t, lang } = useApp();
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error(lang === "mr" ? "कृपया आवश्यक माहिती भरा." : "Please fill required fields."); return; }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(lang === "mr" ? "आपला संदेश पाठवला गेला. धन्यवाद!" : "Your message has been sent. Thank you!");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch { toast.error(lang === "mr" ? "काहीतरी चूक झाली." : "Something went wrong."); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <Seo title="Contact Us" description="Contact Sadguru Swami Narayanand Tirth Seva Trust, Shri Datta Kshetra Audumbar, Tal. Palus, Dist. Sangli, Maharashtra 416320. Phone, email and enquiry form." />
      <PageBanner label={lang === "mr" ? "संपर्क" : "Contact"} titleObj={C.contact} image={IMAGES.niwasStreet} />
      <section className="py-16 lg:py-24 max-w-[1400px] mx-auto px-4 lg:px-8 grid lg:grid-cols-2 gap-12">
        <div>
          <Reveal><SectionLabel>{lang === "mr" ? "संपर्क तपशील" : "Reach the Trust"}</SectionLabel></Reveal>
          <div className="space-y-4">
            <Reveal><div className="flex gap-4 rounded-2xl border border-earth-900/10 bg-white p-5"><MapPin className="text-saffron-600 shrink-0" /><span className="text-earth-800 text-lg">{lang === "mr" ? TRUST.address_mr : TRUST.address_en}</span></div></Reveal>
            <Reveal delay={0.06}>
              <div className="rounded-2xl border border-earth-900/10 bg-white p-5">
                <div className="flex gap-4 items-start"><Phone className="text-saffron-600 shrink-0 mt-1" /><div className="space-y-2 text-lg">
                  <div><a href={`tel:${TRUST.phone_office.replace(/\s+/g, "")}`} className="text-earth-800 font-semibold hover:text-saffron-600">{TRUST.phone_office}</a><div className="text-sm text-earth-600">{lang === "mr" ? "कार्यालयीन वेळेत (सकाळी ९:३० ते सायं. ६:००)" : "During office hours (9:30 AM – 6:00 PM)"}</div></div>
                  <div><a href={`tel:${TRUST.phone_after.replace(/\s+/g, "")}`} className="text-earth-800 font-semibold hover:text-saffron-600">{TRUST.phone_after}</a><div className="text-sm text-earth-600">{lang === "mr" ? "कार्यालयीन वेळेनंतर" : "After office hours"}</div></div>
                </div></div>
              </div>
            </Reveal>
            <Reveal delay={0.12}><a href={`mailto:${TRUST.email}`} className="flex gap-4 rounded-2xl border border-earth-900/10 bg-white p-5"><Mail className="text-saffron-600 shrink-0" /><span className="text-earth-800 text-lg">{TRUST.email} <em className="text-sm text-earth-500">[VERIFY]</em></span></a></Reveal>
            <Reveal delay={0.18}><a href={TRUST.facebook} target="_blank" rel="noreferrer" data-testid="contact-fb" className="flex gap-4 rounded-2xl border border-earth-900/10 bg-white p-5"><Facebook className="text-saffron-600 shrink-0" /><span className="text-earth-800 text-lg">{TRUST.fbPageName}</span></a></Reveal>
          </div>
          <Reveal delay={0.2} className="mt-6">
            <div className="rounded-2xl overflow-hidden border border-earth-900/10">
              <iframe title="map" width="100%" height="260" style={{ border: 0 }} loading="lazy" src="https://www.google.com/maps?q=17.010249,74.484036(Narayananand+Tirth+Swami+Bhakt+Niwas)&z=17&output=embed" />
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <form onSubmit={submit} className="rounded-3xl border border-earth-900/10 bg-white p-6 lg:p-8 space-y-4" data-testid="contact-form">
            <SectionLabel>{lang === "mr" ? "संदेश पाठवा" : "Send a Message"}</SectionLabel>
            <div><Label className="text-earth-800 font-semibold mb-2 block">{UI.name[lang]} *</Label><Input data-testid="contact-name" value={form.name} onChange={set("name")} className="h-12 border-earth-900/20" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-earth-800 font-semibold mb-2 block">{UI.phone[lang]}</Label><Input data-testid="contact-phone" value={form.phone} onChange={set("phone")} className="h-12 border-earth-900/20" /></div>
              <div><Label className="text-earth-800 font-semibold mb-2 block">{UI.email[lang]} *</Label><Input type="email" data-testid="contact-email" value={form.email} onChange={set("email")} className="h-12 border-earth-900/20" /></div>
            </div>
            <div><Label className="text-earth-800 font-semibold mb-2 block">{UI.message[lang]} *</Label><Textarea data-testid="contact-message" value={form.message} onChange={set("message")} className="border-earth-900/20 min-h-32" /></div>
            <Button type="submit" disabled={loading} data-testid="contact-submit" className="w-full h-12 bg-saffron-500 hover:bg-saffron-600 text-white text-base font-semibold">{loading ? "..." : UI.send[lang]}</Button>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
